import React, { useState } from 'react';
import styled from 'styled-components';
import { buildVideoUrl } from '../../services/api';

interface Video {
  id: string;
  type: 'upload' | 'youtube';
  url: string;
  title?: string;
  thumbnail?: string;
  aspect_ratio?: '16:9' | '9:16';
}

interface VideoPlayerProps {
  videos: Video[];
  featuredVideoId?: string;
}

const Container = styled.div`
  margin: ${({ theme }) => theme.spacing.xl} 0;
`;

const Title = styled.h2`
  font-size: 1.75rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const MainVideoWrapper = styled.div<{ $vertical?: boolean }>`
  position: relative;
  width: ${({ $vertical }) => $vertical ? 'min(100%, 480px)' : '100%'};
  aspect-ratio: ${({ $vertical }) => $vertical ? '9 / 16' : '16 / 9'};
  background: #111827;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  overflow: hidden;
  margin: 0 auto ${({ theme }) => theme.spacing.lg};
`;

const VideoEmbed = styled.iframe`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: none;
`;

const VideoElement = styled.video`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const VideoList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: ${({ theme }) => theme.spacing.md};
`;

const VideoThumbnail = styled.div<{ $active?: boolean }>`
  position: relative;
  cursor: pointer;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  overflow: hidden;
  border: 2px solid ${({ theme, $active }) => $active ? theme.colors.primary : 'transparent'};
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.md};
  }

  &::before {
    content: '';
    display: block;
    padding-bottom: 56.25%;
  }
`;

const ThumbnailImage = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const PlayButton = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 48px;
  height: 48px;
  background: rgba(0, 0, 0, 0.7);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 20px;
  transition: all 0.3s ease;

  ${VideoThumbnail}:hover & {
    background: ${({ theme }) => theme.colors.primary};
  }
`;

const VideoTitle = styled.p`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: ${({ theme }) => theme.spacing.sm};
  background: linear-gradient(to top, rgba(0, 0, 0, 0.8), transparent);
  color: white;
  font-size: 0.875rem;
  font-weight: 500;
`;

const NoVideos = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing.xl};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const VideoPlayer: React.FC<VideoPlayerProps> = ({ videos, featuredVideoId }) => {
  const [selectedVideo, setSelectedVideo] = useState<Video | null>(() => {
    if (!videos || videos.length === 0) return null;
    
    // Se há um vídeo em destaque, seleciona ele
    if (featuredVideoId) {
      const featured = videos.find(v => v.id === featuredVideoId);
      if (featured) return featured;
    }
    
    // Caso contrário, seleciona o primeiro vídeo
    return videos[0];
  });

  if (!videos || videos.length === 0) {
    return null;
  }

  const extractYoutubeId = (url: string): string | null => {
    const regex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/;
    const match = url.match(regex);
    return match ? match[1] : null;
  };

  const getYoutubeThumbnail = (videoId: string): string => {
    return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
  };

  const getThumbnailUrl = (video: Video): string => {
    if (video.thumbnail) return video.thumbnail;
    
    if (video.type === 'youtube') {
      const videoId = extractYoutubeId(video.url);
      return videoId ? getYoutubeThumbnail(videoId) : '';
    }
    
    // Para vídeos de upload, poderia retornar uma thumbnail padrão
    return '';
  };

  const renderMainVideo = () => {
    if (!selectedVideo) return null;

    if (selectedVideo.type === 'youtube') {
      const videoId = extractYoutubeId(selectedVideo.url);
      if (!videoId) return null;

      return (
        <VideoEmbed
          src={`https://www.youtube.com/embed/${videoId}?autoplay=1`}
          title={selectedVideo.title || 'Vídeo do YouTube'}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      );
    } else {
      return (
        <VideoElement controls autoPlay>
          <source src={buildVideoUrl(selectedVideo.url)} type="video/mp4" />
          Seu navegador não suporta a reprodução de vídeos.
        </VideoElement>
      );
    }
  };

  return (
    <Container>
      <Title>Vídeos do Imóvel</Title>
      
      <MainVideoWrapper $vertical={selectedVideo?.aspect_ratio === '9:16'}>
        {renderMainVideo()}
      </MainVideoWrapper>

      {videos.length > 1 && (
        <VideoList>
          {videos.map((video) => (
            <VideoThumbnail
              key={video.id}
              $active={selectedVideo?.id === video.id}
              onClick={() => setSelectedVideo(video)}
            >
              <ThumbnailImage 
                src={getThumbnailUrl(video)} 
                alt={video.title || 'Vídeo'}
                onError={(e) => {
                  // Fallback para imagem padrão se thumbnail falhar
                  e.currentTarget.src = '/images/video-placeholder.png';
                }}
              />
              <PlayButton>▶</PlayButton>
              {video.title && <VideoTitle>{video.title}</VideoTitle>}
            </VideoThumbnail>
          ))}
        </VideoList>
      )}
    </Container>
  );
};

export default VideoPlayer;
